import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Avatar,
  ListItemAvatar,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Collapse,
  CardActionArea,
  CardActions,
  Button
} from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import api from '../services/api';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { useNavigate } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const { mode } = useAppTheme();
  const darkMode = mode === 'dark';
  const theme = useTheme();
  const navigate = useNavigate();
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedWork, setExpandedWork] = useState(null);

  // Get theme colors for charts
  const textColor = darkMode ? theme.palette.text.primary : theme.palette.text.primary;
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
  ];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await api.get('/dashboard/stats/');
        setDashboardData(response.data);
        console.log('Dashboard data:', response.data);
      } catch (err) {
        setError('Erro ao carregar os dados do dashboard');
        console.error('Erro ao carregar dados do dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const navigateToProject = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const navigateToResponsible = (responsibleId) => {
    navigate(`/responsible/${responsibleId}`);
  };

  const handleProjectExpand = (projectId) => {
    setExpandedProject(expandedProject === projectId ? null : projectId);
    // Fechar qualquer trabalho expandido quando trocar de projeto
    setExpandedWork(null);
  };

  const handleWorkExpand = (workId) => {
    setExpandedWork(expandedWork === workId ? null : workId);
  };

  // Format work status for display
  const getStatusChip = (status) => {
    switch(status) {
      case 'completed':
        return <Chip 
          size="small" 
          icon={<CheckCircleIcon />} 
          label="Concluído" 
          color="success" 
          variant="outlined" 
        />;
      case 'in_progress':
        return <Chip 
          size="small" 
          icon={<HourglassEmptyIcon />} 
          label="Em andamento" 
          color="primary" 
          variant="outlined" 
        />;
      case 'paused':
        return <Chip 
          size="small" 
          icon={<WarningIcon />} 
          label="Pausado" 
          color="warning" 
          variant="outlined" 
        />;
      default:
        return <Chip 
          size="small" 
          label={status} 
          variant="outlined" 
        />;
    }
  };

  // Função para formatar data e hora
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Data não disponível';
    
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString().substring(0, 5);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Prepare chart data
  const projectStatusData = {
    labels: ['Ativos', 'Concluídos', 'Cancelados', 'Sem Análise'],
    datasets: [
      {
        data: [
          dashboardData.project_stats.active,
          dashboardData.project_stats.completed,
          dashboardData.project_stats.canceled,
          dashboardData.project_stats.without_analysis
        ],
        backgroundColor: chartColors,
        borderColor: darkMode ? theme.palette.background.paper : '#ffffff',
        borderWidth: 2,
      },
    ],
  };

  const priorityData = {
    labels: ['Alta', 'Média', 'Baixa'],
    datasets: [
      {
        data: [
          dashboardData.priority_stats.high,
          dashboardData.priority_stats.medium,
          dashboardData.priority_stats.low
        ],
        backgroundColor: [
          theme.palette.error.main,
          theme.palette.warning.main,
          theme.palette.success.main
        ],
        borderColor: darkMode ? theme.palette.background.paper : '#ffffff',
        borderWidth: 2,
      },
    ],
  };

  // Bar chart for technicians work
  const technicianLabels = dashboardData.technician_work.map(tech => tech.name);
  const technicianData = {
    labels: technicianLabels,
    datasets: [
      {
        label: 'Total de Horas',
        data: dashboardData.technician_work.map(tech => tech.total_hours),
        backgroundColor: theme.palette.primary.main,
      },
      {
        label: 'Horas (últimos 30 dias)',
        data: dashboardData.technician_work.map(tech => tech.recent_hours),
        backgroundColor: theme.palette.secondary.main,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: textColor }
      },
      x: {
        ticks: { color: textColor }
      }
    },
    plugins: {
      legend: {
        labels: {
          color: textColor
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: textColor
        }
      }
    }
  };

  return (
    <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, md: 2 }, mt: 2, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ mb: 3 }}
      >
        <Tab label="Visão Geral" />
        <Tab label="Trabalhos Recentes" />
        <Tab label="Técnicos" />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Status cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AssignmentIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total de Projetos
                    </Typography>
                    <Typography variant="h4">
                      {dashboardData.project_stats.total}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AssignmentTurnedInIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Projetos Ativos
                    </Typography>
                    <Typography variant="h4">
                      {dashboardData.project_stats.active}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PriorityHighIcon color="error" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Alta Prioridade
                    </Typography>
                    <Typography variant="h4">
                      {dashboardData.priority_stats.high}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <EventNoteIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Sem Análise
                    </Typography>
                    <Typography variant="h4">
                      {dashboardData.project_stats.without_analysis}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Charts */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Status dos Projetos
              </Typography>
              <Box height={300} display="flex" justifyContent="center" alignItems="center">
                <Pie data={projectStatusData} options={pieOptions} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Distribuição por Prioridade
              </Typography>
              <Box height={300} display="flex" justifyContent="center" alignItems="center">
                <Pie data={priorityData} options={pieOptions} />
              </Box>
            </Paper>
          </Grid>

          {/* Analysis stats */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Análises de Viabilidade
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" align="center">
                        Total
                      </Typography>
                      <Typography variant="h4" align="center">
                        {dashboardData.feasibility_stats.total}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" align="center" color="success.main">
                        Aprovadas
                      </Typography>
                      <Typography variant="h4" align="center" color="success.main">
                        {dashboardData.feasibility_stats.approved}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" align="center" color="error.main">
                        Rejeitadas
                      </Typography>
                      <Typography variant="h4" align="center" color="error.main">
                        {dashboardData.feasibility_stats.rejected}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" align="center" color="warning.main">
                        Requerem Ajustes
                      </Typography>
                      <Typography variant="h4" align="center" color="warning.main">
                        {dashboardData.feasibility_stats.needs_adjustment}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <EventNoteIcon sx={{ mr: 1 }} /> Trabalhos Recentes
              </Typography>
              
              {(() => {
                // Agrupar trabalhos por projeto
                const projectGroups = {};
                
                dashboardData.latest_work.forEach(work => {
                  if (!projectGroups[work.project_id]) {
                    projectGroups[work.project_id] = {
                      projectName: work.project_name,
                      project_id: work.project_id,
                      works: []
                    };
                  }
                  projectGroups[work.project_id].works.push(work);
                });
                
                return Object.values(projectGroups).map((group, groupIndex) => (
                  <Box key={group.project_id} sx={{ mb: 4 }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        px: 2,
                        py: 1,
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                        borderRadius: 1,
                        boxShadow: 1,
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 3 }
                      }}
                      onClick={() => navigateToProject(group.project_id)}
                    >
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <AssignmentIcon />
                      </Avatar>
                      <Typography variant="h6">
                        {group.projectName}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Chip 
                        label={`${group.works.length} trabalho(s)`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* Timeline de trabalhos */}
                    <Box sx={{ position: 'relative', mt: 3 }}>
                      {/* Linha da timeline */}
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: 36,
                          height: 3,
                          bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                          zIndex: 0
                        }}
                      />
                      
                      {/* Trabalhos na timeline */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          overflow: 'auto',
                          pb: 2,
                          '&::-webkit-scrollbar': {
                            height: 8,
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            borderRadius: 4,
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                            borderRadius: 4,
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                            }
                          }
                        }}
                      >
                        {/* Ordenar trabalhos por data */}
                        {group.works
                          .sort((a, b) => new Date(a.date) - new Date(b.date))
                          .map((work, index) => {
                            const responsibleName = work.responsible_name || work.user_name || 'Não atribuído';
                            const workDate = work.date ? new Date(work.date) : null;
                            
                            return (
                              <Box 
                                key={work.record_id} 
                                sx={{ 
                                  minWidth: 230,
                                  maxWidth: 280,
                                  mr: 3,
                                  position: 'relative',
                                  zIndex: 1
                                }}
                              >
                                {/* Dot na timeline */}
                                <Box 
                                  sx={{ 
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: '50%',
                                    bgcolor: work.status === 'completed' ? 'success.main' : 
                                             work.status === 'paused' ? 'warning.main' : 'primary.main',
                                    border: '3px solid',
                                    borderColor: theme.palette.background.paper,
                                    boxShadow: 1,
                                    position: 'absolute',
                                    top: 30,
                                    left: 14,
                                    zIndex: 2
                                  }}
                                />
                                
                                {/* Data no topo */}
                                {workDate && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      display: 'block',
                                      mb: 0.5,
                                      fontWeight: 'medium',
                                      color: 'text.secondary'
                                    }}
                                  >
                                    {workDate.toLocaleDateString()}
                                  </Typography>
                                )}
                                
                                {/* Card do trabalho */}
                                <Card
                                  variant="outlined"
                                  sx={{
                                    borderLeftWidth: 3,
                                    borderLeftColor: work.status === 'completed' ? 'success.main' : 
                                                    work.status === 'paused' ? 'warning.main' : 'primary.main',
                                    transition: 'all 0.2s',
                                    cursor: 'pointer',
                                    '&:hover': { 
                                      boxShadow: 3,
                                      transform: 'translateY(-3px)'
                                    }
                                  }}
                                  onClick={() => handleWorkExpand(work.record_id)}
                                >
                                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                                      {work.responsible_image ? (
                                        <Avatar 
                                          src={work.responsible_image} 
                                          alt={responsibleName} 
                                          sx={{ width: 24, height: 24, mr: 1 }}
                                        />
                                      ) : (
                                        <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'grey.400' }}>
                                          <PersonIcon sx={{ fontSize: 14 }} />
                                        </Avatar>
                                      )}
                                      <Box>
                                        <Typography 
                                          variant="caption"
                                          color="textSecondary"
                                          sx={{ display: 'block', lineHeight: 1 }}
                                        >
                                          {responsibleName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                          <AccessTimeIcon sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                                          <Typography variant="caption" color="textSecondary">
                                            {work.formatted_time}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ flexGrow: 1 }} />
                                      {getStatusChip(work.status)}
                                    </Box>
                                    
                                    <Typography variant="body2">
                                      {work.description.length > 80 
                                        ? `${work.description.substring(0, 80)}...` 
                                        : work.description}
                                    </Typography>
                                    
                                    {/* Expandir/colapsar ícone */}
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                      <IconButton 
                                        size="small" 
                                        color="primary"
                                        sx={{ 
                                          p: 0.3,
                                          bgcolor: 'background.paper',
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          transition: 'transform 0.3s'
                                        }}
                                        style={{ transform: expandedWork === work.record_id ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                      >
                                        <ExpandMoreIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </CardContent>
                                  
                                  {/* Detalhes expandidos */}
                                  <Collapse in={expandedWork === work.record_id} timeout="auto" unmountOnExit>
                                    <Box sx={{ p: 1.5, pt: 0, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)' }}>
                                      <Divider sx={{ my: 1 }} />
                                      
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: '0.875rem' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: '0.875rem' }} />
                                          <Typography variant="body2">
                                            {formatDateTime(work.date)}
                                          </Typography>
                                        </Box>
                                        
                                        {work.interruptions > 0 && (
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PauseCircleIcon fontSize="small" sx={{ mr: 1, color: 'warning.main', fontSize: '0.875rem' }} />
                                            <Typography variant="body2" color="warning.main">
                                              {work.interruptions} interrupção(ões)
                                            </Typography>
                                          </Box>
                                        )}
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                          <AssignmentIcon fontSize="small" sx={{ mr: 1, mt: 0.3, color: 'text.secondary', fontSize: '0.875rem' }} />
                                          <Typography variant="body2">
                                            {work.description}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      
                                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                        <Button 
                                          size="small" 
                                          variant="outlined"
                                          color="primary"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigateToProject(work.project_id);
                                          }}
                                          sx={{ fontSize: '0.75rem' }}
                                        >
                                          Ver Projeto
                                        </Button>
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </Card>
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                  </Box>
                ));
              })()}
              
              {dashboardData.latest_work.length === 0 && (
                <Typography variant="body1" align="center" sx={{ py: 4 }}>
                  Nenhum trabalho recente encontrado.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Horas Trabalhadas por Técnico
              </Typography>
              <Box height={400}>
                <Bar data={technicianData} options={barOptions} />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detalhes por Técnico
              </Typography>
              <Grid container spacing={2}>
                {dashboardData.technician_work.map((tech) => (
                  <Grid item xs={12} sm={6} md={4} key={tech.responsible_id}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 3 }
                      }}
                      onClick={() => navigateToResponsible(tech.responsible_id)}
                    >
                      <CardHeader
                        avatar={
                          tech.image_url ? (
                            <Avatar src={tech.image_url} alt={tech.name} />
                          ) : (
                            <Avatar>
                              <PersonIcon />
                            </Avatar>
                          )
                        }
                        title={tech.name}
                        subheader={tech.position}
                      />
                      <CardContent>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                              Total de Horas
                            </Typography>
                            <Typography variant="h6">
                              {tech.total_hours}h
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                              Últimos 30 dias
                            </Typography>
                            <Typography variant="h6">
                              {tech.recent_hours}h
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Box mt={1} display="flex" alignItems="center">
                              <DeveloperBoardIcon fontSize="small" sx={{ mr: 1 }} />
                              <Typography variant="body2">
                                {tech.total_projects} projeto{tech.total_projects !== 1 ? 's' : ''}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Dashboard; 